import Fuse from 'fuse.js'
import { Locale } from './locale'
import MainModule from './main'
import {
  AbstractSearchCore,
  AbstractSubscriber,
  IFacet,
  IMultipleResults,
  ISearchConfig,
} from './search'
import { formatToponym } from './toponym'

export interface IAutocompleteItem {
  label: string
  value: string
  type: string
}

class AutocompleteSubscriber extends AbstractSubscriber {
  private _searchCore: AbstractSearchCore | null = null

  constructor() {
    super('autocomplete')
  }

  public detaching() {}

  public async attached(searchCore: AbstractSearchCore): Promise<void> {
    this._searchCore = searchCore
  }

  public async onSearchTextUpdate(text: string): Promise<void> {}

  public async onFacetUpdate(facets: IFacet[]): Promise<void> {}

  public async onResultUpdate(result: IMultipleResults): Promise<void> {}

  public async onSearchConfigUpdate(config: ISearchConfig): Promise<void> {}

  public async getSuggestions(text: string): Promise<IAutocompleteItem[]> {
    const suggestionsLimit = this.getSuggestionsLimit()

    if (!this.isValidSearchText(text)) {
      return []
    }

    await this.whenReady()

    if (!this._searchCore) {
      return []
    }

    const searchResponse = await this.executeSearch(text)
    const processedHits = this.processSearchResults(searchResponse)
    const fuzzySearchResults = this.performFuzzySearch(processedHits, text, suggestionsLimit)

    return this.mapToAutocompleteItems(fuzzySearchResults)
  }

  private getSuggestionsLimit(): number {
    return window.innerWidth >= 768 ? 8 : 4 // more suggestions on desktop
  }

  private isValidSearchText(text: string): boolean {
    return text.length >= 2
  }

  private async executeSearch(text: string) {
    return await this._searchCore!.executeSearch({
      query: text,
    } as ISearchConfig)
  }

  private processSearchResults(response: any): any[] {
    const currentLanguageNameKey = this.getCurrentLanguageNameKey()
    const deduplicatedHits = this.deduplicateHits(response, currentLanguageNameKey)

    return this.enrichHitsWithAutocompleteData(deduplicatedHits, currentLanguageNameKey)
  }

  private getCurrentLanguageNameKey(): string {
    return Locale.getInstance().getLanguage() === 'en' ? 'nameEn' : 'nameFr'
  }

  private deduplicateHits(response: any, nameKey: string): any[] {
    let joinedHits: any[] = []

    for (const key in response) {
      const uniqueItems = response[key].items.filter(
        (item: any, index: number, self: any[]) =>
          index === self.findIndex(t => t[nameKey] === item[nameKey] && t.table === item.table)
      )
      joinedHits = joinedHits.concat(uniqueItems)
    }

    return joinedHits
  }

  private enrichHitsWithAutocompleteData(hits: any[], nameKey: string): any[] {
    return hits.map(hit => {
      const enrichedHit = { ...hit }

      enrichedHit.autocomplete_string = this.getAutocompleteString(hit, nameKey)
      return enrichedHit
    })
  }

  private getAutocompleteString(hit: any, nameKey: string): string {
    return hit[nameKey] || hit['nameFr'] || hit['nameEn'] || hit.objectID
  }

  private performFuzzySearch(hits: any[], text: string, limit: number) {
    const nameKey = this.getCurrentLanguageNameKey()
    const fuse = new Fuse(hits, {
      keys: [nameKey],
    })

    return fuse.search(text, { limit })
  }

  private mapToAutocompleteItems(fuseResults: any[]): IAutocompleteItem[] {
    return fuseResults.map(result => {
      const hit = result?.item
      if (!hit) return { label: '', value: '', type: '' }
      const urlConstruct = this.getUrlConstruct(hit)
      const type = this.determineItemType(hit)

      const rawLabel =
        hit?._highlightResult?.[this.getCurrentLanguageNameKey()]?.value || hit?.autocomplete_string // Use highlighted value from Algolia if available

      return {
        label: formatToponym(rawLabel),
        value: urlConstruct[Locale.getInstance().getLanguage()],
        type: type,
      }
    })
  }

  private getUrlConstruct(hit: any) {
    if (hit.table === 'eventOffers') {
      return {
        en: hit.urlConstruct.en.replace(/\/\d+$/, ''),
        fr: hit.urlConstruct.fr.replace(/\/\d+$/, ''),
      }
    }
    return hit.urlConstruct
  }

  private determineItemType(hit: any): string {
    if (hit.table === 'contributors') {
      return hit.contributorType
    }

    if (hit.table === 'eventSeries' && hit.type === 'Festival') {
      return 'festival'
    }

    return hit.table
  }

  public async whenReady(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (this._searchCore) {
        resolve(true)
      } else {
        let interval = setInterval(() => {
          if (this._searchCore) {
            clearInterval(interval)
            resolve(true)
          }
        }, 10)
      }
    })
  }
}

export class Autocomplete {
  private static _instance: Autocomplete
  public static get instance(): Autocomplete {
    return this._instance || (this._instance = new this())
  }

  private _subscriberInstance: AutocompleteSubscriber = new AutocompleteSubscriber()

  constructor() {
    this.init()
  }

  private async init() {
    MainModule.getInstance().getSearchManager().subscribe(this._subscriberInstance)
  }

  public async whenReady(): Promise<boolean> {
    return this._subscriberInstance.whenReady()
  }

  public async getSuggestions(text: string): Promise<IAutocompleteItem[]> {
    return this._subscriberInstance.getSuggestions(text)
  }
}
